import {
  FormCheckbox,
  FormCheckboxGroup,
  FormDatePicker,
  FormFieldMethods,
  FormFileUploader,
  FormTextField,
  FormTextarea,
  Grid,
  InputType,
  UseFormReturn,
  requiredRule,
} from '@gcl/react-core';
import { LabelWithMessage } from '@common/LabelWithMessage';
import { Dispatch, ReactNode, SetStateAction, useMemo } from 'react';
import { EAdminPageFieldType, EAdminPageFormMode, FILE_UPLOAD_URL_FIELD_NAME } from './admin.constants';
import { IAdminPageFieldConfig, IAdminPageFieldConfigImage, IAdminPageFieldConfigSelect } from './admin.models';
import { AdminPageSelectField } from './AdminPageSelectField';
import './AdminPage.scss';

interface IAdminPageField {
  pageMode: EAdminPageFormMode;
  config: IAdminPageFieldConfig | IAdminPageFieldConfigSelect;
  itemFormFieldMethods: FormFieldMethods;
  idUseFormMethods: UseFormReturn;
  setOptionData: Dispatch<
    SetStateAction<{
      [key: string]: any;
    }>
  >;
  groupKey?: string;
  index?: number;
  dataItem: {
    [key: string]: any;
  };
}
export const AdminPageField = ({
  pageMode,
  config,
  itemFormFieldMethods,
  idUseFormMethods,
  setOptionData,
  groupKey,
  index,
  dataItem,
}: IAdminPageField) => {
  const fieldId = useMemo(() => {
    if (groupKey && (index ?? -1) >= 0) return `${groupKey}[${index}].${config.fieldName}`;
    return config.fieldName;
  }, [groupKey, index, config.fieldName]);

  const fieldType = typeof config.fieldType === 'object' ? config.fieldType[pageMode] : config.fieldType;
  if (fieldType === EAdminPageFieldType.NONE) return <></>;

  switch (fieldType) {
    case EAdminPageFieldType.GROUP: {
      const groupValues = (itemFormFieldMethods.getValues(config.fieldName) ?? []) as any[];

      if (groupValues.length <= 0)
        return (
          <FieldContainer fieldConfig={config} fieldId={fieldId}>
            <span>No Related Items</span>
          </FieldContainer>
        );
      return (
        <>
          {groupValues.map((item, itemIndex) => {
            return config.groupConfig?.map((groupConfig) => {
              return (
                <AdminPageField
                  dataItem={dataItem}
                  idUseFormMethods={idUseFormMethods}
                  key={groupConfig.fieldName}
                  pageMode={pageMode}
                  config={groupConfig}
                  itemFormFieldMethods={itemFormFieldMethods}
                  setOptionData={setOptionData}
                  groupKey={config.fieldName}
                  index={itemIndex}
                />
              );
            });
          })}
        </>
      );
    }
    case EAdminPageFieldType.SEPARATOR:
      return <Grid item xs={12} className="gcl-gems-admin-separator" />;
    case EAdminPageFieldType.LABEL:
    case EAdminPageFieldType.LABEL_DATE: {
      console.log(itemFormFieldMethods.getValues());
      let value = itemFormFieldMethods.getValues(fieldId);
      if (typeof value === 'boolean') {
        value = value ? 'True' : 'False';
      }
      return (
        <FieldContainer fieldConfig={config} fieldId={fieldId}>
          <div>{value}</div>
        </FieldContainer>
      );
    }
    case EAdminPageFieldType.DATE:
      return (
        <FieldContainer fieldConfig={config} fieldId={fieldId}>
          <FormDatePicker
            formFieldMethods={itemFormFieldMethods}
            id={fieldId}
            rules={config.required ? requiredRule : undefined}
          />
        </FieldContainer>
      );
    case EAdminPageFieldType.NUMBER:
      return (
        <FieldContainer fieldConfig={config} fieldId={fieldId}>
          <FormTextField
            formFieldMethods={itemFormFieldMethods}
            id={fieldId}
            rules={config.required ? requiredRule : undefined}
            type={InputType.Number}
          />
        </FieldContainer>
      );
    case EAdminPageFieldType.TEXT_INPUT:
      return (
        <FieldContainer fieldConfig={config} fieldId={fieldId}>
          <FormTextField
            formFieldMethods={itemFormFieldMethods}
            id={fieldId}
            rules={config.required ? requiredRule : undefined}
          />
        </FieldContainer>
      );
    case EAdminPageFieldType.TEXT_AREA:
      return (
        <FieldContainer fieldConfig={config} fieldId={fieldId}>
          <FormTextarea
            id={fieldId}
            formFieldMethods={itemFormFieldMethods}
            rules={config.required ? requiredRule : undefined}
          />
        </FieldContainer>
      );
    case EAdminPageFieldType.BOOLEAN:
      return (
        <FieldContainer fieldConfig={config} fieldId={fieldId}>
          <FormCheckbox
            formFieldMethods={itemFormFieldMethods}
            id={fieldId}
            rules={config.required ? requiredRule : undefined}
          />
        </FieldContainer>
      );
    case EAdminPageFieldType.FILE_UPLOAD_SINGLE:
      return (
        <>
          <FieldContainer fieldConfig={config} fieldId={fieldId}>
            <FormFileUploader
              acceptFileTypes={config.acceptFileTypes}
              id={fieldId}
              formFieldMethods={itemFormFieldMethods}
              rules={config.required ? requiredRule : undefined}
            />
          </FieldContainer>
          <FieldContainer
            fieldConfig={{
              ...config,
              label: `${config.label} Url`,
              fieldName: `${fieldId}${FILE_UPLOAD_URL_FIELD_NAME}`,
            }}
          >
            <div>{itemFormFieldMethods.getValues(`${fieldId}${FILE_UPLOAD_URL_FIELD_NAME}`)}</div>
          </FieldContainer>
        </>
      );
    case EAdminPageFieldType.CHECKBOX_GROUP: {
      const selectConfig = config as IAdminPageFieldConfigSelect;
      return (
        <FieldContainer fieldConfig={config} fieldId={fieldId}>
          <AdminPageSelectField
            idUseFormMethods={idUseFormMethods}
            formFieldMethods={itemFormFieldMethods}
            setOptionData={setOptionData}
            config={selectConfig}
            renderSelectField={(options) => {
              return (
                <FormCheckboxGroup
                  itemPerRow={2}
                  allowSelectAll
                  checkOptions={options}
                  displayFieldName={selectConfig.optionDisplayFieldName}
                  formFieldMethods={itemFormFieldMethods}
                  id={fieldId}
                  defaultSortingKey={selectConfig.optionDefaultSortingKey}
                />
              );
            }}
          />
        </FieldContainer>
      );
    }
    case EAdminPageFieldType.IMAGE: {
      const mimeType = dataItem?.[(config as IAdminPageFieldConfigImage)?.mimeTypeFieldName];
      const fileContent = dataItem?.[fieldId];

      return (
        <FieldContainer fieldConfig={config} fieldId={fieldId}>
          <FormFileUploader
            acceptFileTypes={['image/jpeg', 'image/png', 'image/bmp']}
            id={fieldId}
            formFieldMethods={itemFormFieldMethods}
            rules={requiredRule}
          />
          {!!fileContent && (
            <img
              id={fieldId}
              alt={fieldId}
              src={`data:${mimeType};base64,${fileContent}`}
              className="gcl-gems-admin-image"
            />
          )}
        </FieldContainer>
      );
    }
    default:
      return <></>;
  }
};
const FieldContainer = ({
  children,
  fieldConfig,
  fieldId,
}: {
  children: ReactNode;
  fieldConfig: IAdminPageFieldConfig;
  fieldId?: string;
}) => {
  return (
    <Grid item xs={fieldConfig.gridXs ?? 12} sm={fieldConfig.gridSm ?? 6}>
      <LabelWithMessage
        labelFor={fieldId ?? fieldConfig.fieldName}
        text={fieldConfig.label}
        required={fieldConfig.required}
      />
      {children}
    </Grid>
  );
};
